"use client";

import Container from "@/containers/Container";
import ContentSelectV2 from "@/containers/contentselect/ContentSelectV2";
import SelectionPill from "@/containers/contentselect/SelectionPill";
import ExpandedHuiswerkModusSelect from "@/containers/contentselect/modusselect/ExpandedHuiswerkModusSelect";
import MobileDesktopTip from "@/containers/contentselect/modusselect/MobileDesktopTip";
import About from "@/containers/framerlanding/About";
import WaveDivider from "@/components/WaveDivider";
import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import { useHuiswerkModusContext } from "@/context/HuiswerkModusContext";
import { useLandingContext } from "@/context/LandingContext";
import { FEATURE_BG_COLORS, FEATURE_CARDS } from "@/lib/featureConstants";
import { Hoofdstuk, WisWizModus } from "@/lib/types";
import { formatTitel, sortHoofdstukken } from "@/lib/utils";
import {
  premiumFeatures,
  type PremiumFeatureKey
} from "@/lib/premiumHomeContent";
import { useFeatureDiscovery } from "@/hooks/useFeatureDiscovery";
import posthog from "posthog-js";
import { useRouter } from "next/navigation";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

export default function PremiumLanding() {
  const { isParent } = useAuthContext();
  const { phase } = useContentSelectieContext();
  const { sectionRefs, setActiveSection } = useLandingContext();
  const previousPhaseRef = useRef(phase);

  useEffect(() => {
    if (previousPhaseRef.current === "profiel" && phase === "modus") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    previousPhaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const sections = [
        { ref: sectionRefs.heroRef, id: 0 },
        { ref: sectionRefs.aboutUsSectionRef, id: 4 }
      ];

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (
          section.ref.current &&
          section.ref.current.offsetTop <= currentScrollY + 200
        ) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [setActiveSection, sectionRefs]);

  if (isParent) return null;

  if (phase === "profiel") {
    return (
      <main className="min-h-[calc(100dvh-5rem)] bg-achtergrond">
        <section ref={sectionRefs.heroRef} className="bg-achtergrond">
          <PremiumProfileHero />
        </section>
      </main>
    );
  }

  return (
    <main className="bg-achtergrond">
      <section ref={sectionRefs.heroRef} className="bg-achtergrond">
        <PremiumStartSection />
      </section>

      <section ref={sectionRefs.aboutUsSectionRef} className="bg-achtergrond">
        <About />
      </section>
    </main>
  );
}

export function PremiumHomeHero({
  centerStartCard = false,
  includeFeedback = false,
  includeDiscovery = true,
  showStartLogo = false
}: {
  centerStartCard?: boolean;
  includeFeedback?: boolean;
  includeDiscovery?: boolean;
  showStartLogo?: boolean;
}) {
  const { phase } = useContentSelectieContext();

  if (phase === "profiel") return <PremiumProfileHero />;

  return (
    <PremiumStartSection
      centerStartCard={centerStartCard}
      includeFeedback={includeFeedback}
      includeDiscovery={includeDiscovery}
      showStartLogo={showStartLogo}
    />
  );
}

function PremiumProfileHero() {
  return (
    <Container>
      <div className="flex min-h-[calc(100dvh-5rem)] items-start justify-center py-8 md:py-24">
        <div className="mx-auto w-full max-w-4xl px-0 py-7 md:py-9">
          <ContentSelectV2 flow="full" />
        </div>
      </div>
    </Container>
  );
}

export function PremiumStartSection({
  centerStartCard = false,
  includeFeedback = true,
  includeDiscovery = true,
  showStartLogo = false
}: {
  centerStartCard?: boolean;
  includeFeedback?: boolean;
  includeDiscovery?: boolean;
  showStartLogo?: boolean;
}) {
  const { userData } = useAuthContext();
  const { api, selectModus, isMobile, hoofdstukken } = useAppContext();
  const {
    profiel,
    setProfiel,
    currentHoofdstuk,
    startProfielEdit,
    setSelectedHoofdstuk
  } = useContentSelectieContext();
  const { clearHuiswerkSession } = useHuiswerkModusContext();
  const router = useRouter();
  const feedbackTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);
  const [isChapterPickerOpen, setIsChapterPickerOpen] = useState(false);
  const [isHuiswerkExpanded, setIsHuiswerkExpanded] = useState(false);
  const useCenteredStartCard = centerStartCard && !includeDiscovery;
  const shouldVerticallyCenterStartCard =
    useCenteredStartCard && !isChapterPickerOpen;

  const hoofdstukLabel = currentHoofdstuk
    ? formatTitel(currentHoofdstuk.titel)
    : "Je hoofdstuk";
  const niveauLabel = profiel?.wiskundeType
    ? `${profiel.niveau} ${profiel.wiskundeType}`
    : profiel?.niveau;
  const leerlingLabel = [
    profiel?.jaar ? `Klas ${profiel.jaar}` : undefined,
    niveauLabel
  ]
    .filter(Boolean)
    .join(" · ");
  const editieLabel = profiel?.editie ? `Editie ${profiel.editie}` : "Editie";
  const methodeImage =
    profiel?.methode === "Moderne Wiskunde"
      ? "/modernewiskunde.png"
      : "/getalenruimte.png";
  const savedHuiswerkSession = userData?.huiswerk_set;
  const sortedHoofdstukken = useMemo(
    () => sortHoofdstukken(hoofdstukken),
    [hoofdstukken]
  );
  const hoofdstukGroepen = useMemo<Hoofdstuk[][]>(() => {
    const groepen = new Map<number, Hoofdstuk[]>();

    sortedHoofdstukken.forEach((hoofdstuk) => {
      const groep = groepen.get(hoofdstuk.boek) ?? [];
      groep.push(hoofdstuk);
      groepen.set(hoofdstuk.boek, groep);
    });

    return Array.from(groepen.values());
  }, [sortedHoofdstukken]);

  const resizeFeedbackTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 176)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 176 ? "auto" : "hidden";
  };

  useEffect(() => {
    const textarea = feedbackTextareaRef.current;
    if (!textarea) return;

    resizeFeedbackTextarea(textarea);
  }, [feedbackText]);

  const startMode = (modus: WisWizModus) => {
    if (modus === "huiswerk" && isMobile) return;
    selectModus(modus, "mode_card");
    router.push("/library");
  };

  const selectChapterFromHome = (hoofdstukId: number) => {
    if (hoofdstukId === profiel?.hoofdstukId) {
      setIsChapterPickerOpen(false);
      return;
    }

    setSelectedHoofdstuk(hoofdstukId);
    setProfiel((currentProfiel) =>
      currentProfiel ? { ...currentProfiel, hoofdstukId } : currentProfiel
    );
    clearHuiswerkSession();
    setIsHuiswerkExpanded(false);
    setIsChapterPickerOpen(false);
  };
  const submitFeedback = async () => {
    const trimmed = feedbackText.trim();
    if (!trimmed || isSubmittingFeedback) return;

    setIsSubmittingFeedback(true);
    setFeedbackError(false);
    try {
      await api.postStappenplanForm({
        subvraag_id: null,
        reden: "home_feedback",
        free_text: trimmed
      });
      posthog.capture("home_feedback_submitted", { surface: "home_dashboard" });
      setFeedbackText("");
      setFeedbackSent(true);
      window.setTimeout(() => setFeedbackSent(false), 3000);
    } catch (error) {
      console.error("Failed to submit home feedback:", error);
      setFeedbackError(true);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <>
      <Container>
        <div
          className={
            useCenteredStartCard
              ? `mx-auto flex min-h-[calc(100dvh-5.5rem)] w-full max-w-[42rem] flex-col items-center pb-5 pt-3 md:min-h-[calc(100dvh-6.5rem)] md:py-8 ${
                  shouldVerticallyCenterStartCard
                    ? "justify-center"
                    : "justify-start"
                }`
              : "-mx-4 grid gap-7 pb-4 pt-3 md:mx-0 md:gap-10 md:py-14 lg:h-[calc(100vh-10rem)] lg:min-h-[34rem] lg:max-h-[42rem] lg:grid-cols-[minmax(0,42rem)_23rem] lg:justify-center"
          }
        >
          {includeDiscovery && (
            <PremiumDiscoveryPanel className="order-2 h-full lg:order-2" />
          )}

          {useCenteredStartCard && showStartLogo && (
            <img
              src="/wiswizlogo.png"
              alt="WisWiz"
              className="mb-5 w-36 max-w-[48%] object-contain md:mb-7 md:w-44"
            />
          )}

          <div className="relative order-1 w-full lg:order-1">
            <div
              className={`relative flex h-full min-h-0 flex-col overflow-hidden bg-white shadow-[0_16px_45px_rgba(15,23,42,0.08)] ${
                useCenteredStartCard
                  ? "rounded-xl border border-gray-200"
                  : "rounded-none border-y border-gray-200 md:rounded-xl md:border-x"
              }`}
            >
              <div
                className={`flex min-h-20 items-center border-b border-gray-100 bg-gradient-to-b from-white to-gray-50/70 py-4 md:px-6 md:py-5 ${
                  useCenteredStartCard ? "px-3" : "px-4"
                }`}
              >
                <div className="mx-auto flex w-full max-w-xl flex-wrap items-center justify-center gap-x-2 gap-y-4 md:gap-y-3">
                  <button
                    type="button"
                    onClick={startProfielEdit}
                    className="inline-flex h-10 min-w-0 max-w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-xs font-extrabold text-gray-700 shadow-none transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/20 md:gap-2 md:px-4 md:text-sm md:shadow-sm"
                  >
                    <PenLineIcon className="h-4 w-4 flex-none" />
                    <span className="flex h-8 w-7 flex-none items-center justify-center">
                      <img
                        src={methodeImage}
                        alt=""
                        className="h-8 w-auto flex-none object-contain drop-shadow-[0_5px_9px_rgba(15,23,42,0.10)]"
                      />
                    </span>
                    <span className="min-w-0 truncate">
                      {leerlingLabel} · {editieLabel}
                    </span>
                  </button>

                  <button
                    type="button"
                    aria-expanded={isChapterPickerOpen}
                    onClick={() => setIsChapterPickerOpen((isOpen) => !isOpen)}
                    className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-extrabold shadow-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/20 md:gap-2 md:px-4 md:text-sm md:shadow-sm ${
                      isChapterPickerOpen
                        ? "border-gray-300 bg-gray-50 text-gray-950"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-950"
                    }`}
                  >
                    <PenLineIcon className="h-4 w-4" />
                    {hoofdstukLabel}
                  </button>
                </div>
              </div>

              <div className="flex h-full min-h-0 flex-col">
                <div
                  className={`flex min-h-0 flex-1 justify-center px-4 md:px-9 ${
                    isChapterPickerOpen
                      ? "items-start py-6"
                      : "items-stretch py-7 md:py-8"
                  }`}
                >
                  <div
                    className={`mx-auto w-full text-center ${
                      isChapterPickerOpen
                        ? "flex max-w-xl flex-col items-center"
                        : "grid max-w-xl min-h-full grid-rows-[minmax(0,1fr)_auto_auto_minmax(0,1fr)_auto] items-center"
                    }`}
                  >
                    {!isChapterPickerOpen && (
                      <StartActionsHeading className="row-start-2 pb-5 md:pb-6" />
                    )}

                    <div
                      className={`flex w-full flex-col items-center ${
                        isChapterPickerOpen ? "" : "row-start-3"
                      }`}
                    >
                      <div
                        aria-hidden={!isChapterPickerOpen}
                        className={`grid w-full max-w-xl transition-[grid-template-rows,opacity,margin-top] duration-300 ease-out ${
                          isChapterPickerOpen
                            ? "grid-rows-[1fr] opacity-100"
                            : "pointer-events-none mt-0 grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="grid divide-y divide-gray-100 px-px py-px">
                            {hoofdstukGroepen.map((groep) => (
                              <div
                                key={`premium_deel_${groep[0].boek}`}
                                className="grid gap-2 py-2 first:pt-0 last:pb-0 sm:grid-cols-[4.25rem_minmax(0,1fr)] sm:items-center"
                              >
                                <div className="flex items-center justify-center gap-2 sm:justify-start">
                                  <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">
                                    Deel {groep[0].boek}
                                  </span>
                                </div>

                                <div className="flex flex-wrap justify-center gap-1.5 sm:justify-start">
                                  {sortHoofdstukken(groep).map((hoofdstuk) => {
                                    const isSelected =
                                      hoofdstuk.id === profiel?.hoofdstukId;
                                    const title = formatTitel(hoofdstuk.titel);

                                    return (
                                      <SelectionPill
                                        key={hoofdstuk.id}
                                        onClick={() =>
                                          selectChapterFromHome(hoofdstuk.id)
                                        }
                                        disabled={!isChapterPickerOpen}
                                        selected={isSelected}
                                        size="compact"
                                        ariaLabel={`Selecteer ${title} van deel ${hoofdstuk.boek}`}
                                      >
                                        <span className="block max-w-[8.5rem] truncate">
                                          {title}
                                        </span>
                                      </SelectionPill>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {!isChapterPickerOpen && (
                        <>
                          <div className="grid w-full max-w-xl grid-cols-1 gap-3 md:hidden">
                            <button
                              type="button"
                              onClick={() => startMode("uitleg")}
                              className="group flex w-full items-center gap-3 rounded-xl border border-mathorange/70 bg-mathorange/[0.03] px-4 py-3 text-left transition-colors hover:border-mathorange hover:bg-mathorange/[0.06] focus:outline-none focus:ring-2 focus:ring-mathorange/25"
                            >
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-mathorange/15 text-mathorange [&_svg]:h-[18px] [&_svg]:w-[18px]">
                                <LightbulbIcon />
                              </span>
                              <span className="flex min-w-0 flex-col items-start">
                                <span className="text-base font-bold leading-5 text-gray-800">
                                  Losse opgave
                                </span>
                                <span className="mt-0.5 text-xs leading-5 text-gray-500">
                                  Uitleg bij de opgave waar je vastloopt
                                </span>
                              </span>
                            </button>

                            <button
                              type="button"
                              disabled
                              className="group flex w-full cursor-not-allowed items-center gap-3 rounded-xl border border-dashed border-huiswerk/60 bg-huiswerk/[0.03] px-4 py-3 text-left"
                            >
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-huiswerk/15 text-huiswerk opacity-60 [&_svg]:h-[18px] [&_svg]:w-[18px]">
                                <BookIcon />
                              </span>
                              <span className="flex min-w-0 flex-col items-start">
                                <span className="text-base font-bold leading-5 text-gray-500">
                                  Huiswerkset
                                </span>
                                <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-400">
                                  <DesktopIcon />
                                  Alleen op tablet of computer
                                </span>
                              </span>
                            </button>
                          </div>

                          <div className="hidden w-full max-w-xl grid-cols-1 gap-5 md:grid md:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => startMode("uitleg")}
                              className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-mathyellow bg-white px-5 py-6 text-center shadow-sm transition-colors hover:bg-mathyellow/10 focus:outline-none focus:ring-2 focus:ring-mathyellow/45"
                            >
                              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-mathorange/15 text-mathorange shadow-sm ring-1 ring-mathorange/20 transition-transform duration-200 group-hover:scale-105 [&_svg]:h-5 [&_svg]:w-5">
                                <LightbulbIcon />
                              </span>
                              <span className="grid min-w-0 gap-2">
                                <span className="text-xl font-semibold leading-6 text-gray-950">
                                  Losse opgave
                                </span>
                                <span className="mx-auto max-w-[12rem] text-[13px] font-normal leading-6 text-gray-500">
                                  Uitleg bij de opgave waar je vastloopt
                                </span>
                              </span>
                            </button>

                            <div className="flex min-h-0 w-full flex-col items-center">
                              {!isHuiswerkExpanded && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!savedHuiswerkSession) {
                                      startMode("huiswerk");
                                      return;
                                    }
                                    setIsHuiswerkExpanded(true);
                                  }}
                                  disabled={Boolean(isMobile)}
                                  className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-teal-700 bg-white px-5 py-6 text-center shadow-sm transition-colors hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-700/25 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:shadow-none disabled:[&_.mode-button-icon]:bg-gray-100 disabled:[&_.mode-button-icon]:text-gray-400 disabled:[&_.mode-button-icon]:ring-gray-200 disabled:[&_.mode-button-label]:text-gray-400 disabled:[&_.mode-button-subtitle]:text-gray-400"
                                >
                                  <span className="mode-button-icon flex h-10 w-10 items-center justify-center rounded-xl bg-teal-700/10 text-teal-700 shadow-sm ring-1 ring-teal-700/20 transition-transform duration-200 group-hover:scale-105 [&_svg]:h-5 [&_svg]:w-5">
                                    <BookIcon />
                                  </span>
                                  <span className="grid min-w-0 gap-2">
                                    <span className="mode-button-label text-xl font-semibold leading-6 text-gray-950">
                                      Huiswerkset
                                    </span>
                                    <span className="mode-button-subtitle mx-auto max-w-[12rem] text-[13px] font-normal leading-6 text-gray-500">
                                      WisWiz begeleidt je door jouw huiswerk
                                    </span>
                                  </span>
                                </button>
                              )}

                              {savedHuiswerkSession && isHuiswerkExpanded && (
                                <div className="group relative flex max-h-[min(23rem,calc(100vh-24rem))] min-h-0 w-full flex-col overflow-hidden rounded-2xl border-2 border-huiswerk bg-white shadow-xl">
                                  <ExpandedHuiswerkModusSelect
                                    setExpanded={setIsHuiswerkExpanded}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {isMobile && <MobileDesktopTip />}
                        </>
                      )}
                    </div>

                    {!isChapterPickerOpen && (
                      <StartActionsHeading
                        className="invisible hidden row-start-5 pt-5 md:block md:pt-6"
                        ariaHidden
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {includeFeedback && (
        <>
          <WaveDivider topColor="#f0f9ff" bottomColor="#ffffff" />

          <section className="bg-white">
            <Container>
              <div className="py-7 md:py-10">
                <div className="mx-auto flex max-w-3xl flex-col">
                  <div className="text-center">
                    <h2 className="text-2xl font-extrabold leading-tight text-gray-950 md:text-3xl">
                      Wat kunnen wij nog voor je doen voor de eindtoetsen?
                    </h2>
                  </div>

                  <form
                    className="mt-5 w-full text-left"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitFeedback();
                    }}
                  >
                    {feedbackSent ? (
                      <div
                        role="status"
                        className="grid min-h-32 w-full place-items-center rounded-2xl border border-gray-200 bg-white px-6 py-6 text-center shadow-sm"
                      >
                        <div>
                          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-100 text-gray-700 shadow-sm [&_svg]:h-5 [&_svg]:w-5">
                            <CheckIcon />
                          </div>
                          <p className="mt-3 text-lg font-extrabold text-gray-950">
                            Hartelijk bedankt.
                          </p>
                          <p className="mt-1 text-sm font-semibold text-gray-600">
                            We gaan er vandaag naar kijken.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm transition-colors focus-within:border-info/40 focus-within:ring-4 focus-within:ring-info/10">
                        <div
                          className="relative max-h-44 min-h-[105px] overflow-hidden rounded-xl"
                          style={{
                            backgroundImage:
                              "repeating-linear-gradient(to bottom, transparent 0, transparent 27px, rgba(148, 163, 184, 0.24) 27px, rgba(148, 163, 184, 0.24) 28px)"
                          }}
                        >
                          <textarea
                            ref={feedbackTextareaRef}
                            value={feedbackText}
                            onChange={(event) => {
                              setFeedbackText(event.target.value);
                              resizeFeedbackTextarea(event.currentTarget);
                            }}
                            placeholder="Vertel wat WisWiz nog beter kan maken voor je toetsweek."
                            rows={1}
                            className="relative z-[1] max-h-44 min-h-[105px] w-full min-w-0 resize-none overflow-y-auto border-0 bg-transparent px-2 pb-1 pt-0 text-sm font-normal leading-7 text-gray-900 placeholder:text-gray-300 focus:outline-none"
                            disabled={isSubmittingFeedback}
                          />
                        </div>
                        <div className="flex items-center justify-center pt-3">
                          <button
                            type="submit"
                            disabled={
                              !feedbackText.trim() || isSubmittingFeedback
                            }
                            className="inline-flex h-9 min-w-28 items-center justify-center rounded-full bg-mathorange px-5 text-sm font-bold text-white transition-colors hover:bg-mathorange/90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                          >
                            {isSubmittingFeedback ? "..." : "Verstuur"}
                          </button>
                        </div>
                      </div>
                    )}
                  </form>

                  {feedbackError && (
                    <p className="mt-3 text-sm font-semibold text-red-600">
                      Versturen lukte niet. Probeer het nog een keer.
                    </p>
                  )}
                </div>
              </div>
            </Container>
          </section>

          <WaveDivider topColor="#ffffff" bottomColor="#f0f9ff" />
        </>
      )}
    </>
  );
}

export function PremiumDiscoveryPanel({
  className = "",
  variant = "default",
  eyebrow,
  title,
  subtitle,
  scrollLabel
}: {
  className?: string;
  variant?: "default" | "checkout";
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  scrollLabel?: string;
}) {
  const discoveryScrollRef = useRef<HTMLDivElement | null>(null);
  const [showDiscoveryScrollHint, setShowDiscoveryScrollHint] = useState(false);
  const [expandedFeature, setExpandedFeature] =
    useState<PremiumFeatureKey | null>(null);
  const { isDiscovered } = useFeatureDiscovery();
  const isCheckoutVariant = variant === "checkout";

  const discovered = useMemo<Record<PremiumFeatureKey, boolean>>(
    () =>
      isCheckoutVariant
        ? {
            hint: false,
            stappenplan: false,
            bijlesbot: false,
            theorie: false,
            begripvraag: false,
            huiswerkmodus: false,
            "schrift-feedback": false,
            oefentoets: false
          }
        : {
            hint: isDiscovered("hint"),
            stappenplan: isDiscovered("stappenplan"),
            bijlesbot: isDiscovered("bijlesbot"),
            theorie: isDiscovered("theorie"),
            begripvraag: isDiscovered("begripvraag"),
            huiswerkmodus: false,
            "schrift-feedback": isDiscovered("schrift-feedback"),
            oefentoets: isDiscovered("oefentoets")
          },
    [isCheckoutVariant, isDiscovered]
  );

  const featureVisuals = useMemo(() => buildFeatureVisuals(), []);
  const discoveryFeatureGroups = useMemo(() => {
    const visibleFeatures = premiumFeatures
      .filter((feature) => feature.key !== "huiswerkmodus")
      .sort((a, b) => {
        if (a.key === "schrift-feedback") return -1;
        if (b.key === "schrift-feedback") return 1;
        return 0;
      });

    if (isCheckoutVariant) {
      return {
        undiscovered: visibleFeatures,
        discovered: []
      };
    }

    return {
      undiscovered: visibleFeatures.filter(
        (feature) => !discovered[feature.key]
      ),
      discovered: visibleFeatures.filter((feature) => discovered[feature.key])
    };
  }, [discovered, isCheckoutVariant]);

  useEffect(() => {
    const scrollContainer = discoveryScrollRef.current;
    if (!scrollContainer) return;

    const updateScrollHint = () => {
      setShowDiscoveryScrollHint(
        scrollContainer.scrollTop + scrollContainer.clientHeight <
          scrollContainer.scrollHeight - 8
      );
    };

    updateScrollHint();
    const animationFrame = window.requestAnimationFrame(updateScrollHint);
    const transitionTimeout = window.setTimeout(updateScrollHint, 340);

    scrollContainer.addEventListener("scroll", updateScrollHint, {
      passive: true
    });
    window.addEventListener("resize", updateScrollHint);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(transitionTimeout);
      scrollContainer.removeEventListener("scroll", updateScrollHint);
      window.removeEventListener("resize", updateScrollHint);
    };
  }, [expandedFeature, discoveryFeatureGroups]);

  const toggleFeatureDetails = (featureKey: PremiumFeatureKey) => {
    posthog.capture("home_feature_details_toggled", {
      feature_key: featureKey,
      surface: isCheckoutVariant ? "checkout" : "home_dashboard"
    });
    setExpandedFeature((currentFeature) =>
      currentFeature === featureKey ? null : featureKey
    );
  };

  const scrollDiscoveryDown = () => {
    const scrollContainer = discoveryScrollRef.current;
    if (!scrollContainer) return;

    scrollContainer.scrollBy({
      top: Math.max(160, scrollContainer.clientHeight * 0.55),
      behavior: "smooth"
    });
  };

  return (
    <div
      className={`flex min-h-0 flex-col overflow-hidden bg-white ${
        isCheckoutVariant
          ? "rounded-xl"
          : "rounded-none border-y border-gray-200 md:rounded-xl md:border-x"
      } ${className}`}
    >
      <div
        className={`flex min-h-20 items-center ${
          isCheckoutVariant
            ? "px-5 py-4"
            : "border-b border-gray-100 bg-gradient-to-b from-white to-gray-50/70 px-7 py-5"
        }`}
      >
        {isCheckoutVariant ? (
          <div className="min-w-0">
            {eyebrow && (
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400">
                {eyebrow}
              </p>
            )}
            <h2 className="mt-1 text-lg font-bold leading-tight text-gray-950">
              {title ?? (
                <>
                  Ontdek <span className="text-mathorange">Wis</span>
                  <span className="text-mathyellow">Wiz</span>
                </>
              )}
            </h2>
            {subtitle && (
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="mt-1 text-xs font-medium leading-5 text-gray-600">
                  {subtitle}
                </p>
                <CheckoutBookStack />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <h2 className="flex items-center gap-2 text-xl font-bold leading-none text-gray-950">
              <span className="flex h-8 w-8 flex-none items-center justify-center text-mathorange [&_svg]:h-5 [&_svg]:w-5">
                <CompassIcon />
              </span>
              <span>
                Ontdek <span className="text-mathorange">Wis</span>
                <span className="text-mathyellow">Wiz</span>
              </span>
            </h2>
          </div>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden px-4 py-3">
        <div
          ref={discoveryScrollRef}
          className={`h-full overflow-y-auto pb-3 pr-0.5 font-medium text-gray-800 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            isCheckoutVariant ? "text-[13px]" : "text-sm"
          }`}
        >
          <FeatureDiscoveryGroup
            title={isCheckoutVariant ? undefined : "Niet ontdekt"}
            features={discoveryFeatureGroups.undiscovered}
            discovered={discovered}
            expandedFeature={expandedFeature}
            featureVisuals={featureVisuals}
            onToggleFeature={toggleFeatureDetails}
            compact={isCheckoutVariant}
          />
          {discoveryFeatureGroups.discovered.length > 0 && (
            <FeatureDiscoveryGroup
              title="Ontdekt"
              features={discoveryFeatureGroups.discovered}
              discovered={discovered}
              expandedFeature={expandedFeature}
              featureVisuals={featureVisuals}
              onToggleFeature={toggleFeatureDetails}
              className="mt-4"
              compact={isCheckoutVariant}
            />
          )}
        </div>

        <div
          className={`pointer-events-none absolute inset-x-4 bottom-3 flex h-16 items-end justify-center rounded-b-xl bg-gradient-to-b from-white/0 via-white/85 to-white pb-1 transition-opacity duration-200 ${
            showDiscoveryScrollHint ? "opacity-100" : "opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={scrollDiscoveryDown}
            disabled={!showDiscoveryScrollHint}
            aria-label={scrollLabel ?? "Scroll verder door Ontdek WisWiz"}
            className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors hover:border-info/30 hover:bg-info/5 hover:text-info focus:outline-none focus:ring-2 focus:ring-info/20 disabled:pointer-events-none"
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

function StartActionsHeading({
  className = "",
  ariaHidden = false
}: {
  className?: string;
  ariaHidden?: boolean;
}) {
  return (
    <div className={`w-full text-center ${className}`} aria-hidden={ariaHidden}>
      <h3 className="text-2xl font-extrabold leading-tight text-gray-950 md:text-3xl">
        Wat wil je doen?
      </h3>
    </div>
  );
}

function CheckoutBookStack() {
  return (
    <div
      className="relative mt-1 h-10 w-12 shrink-0"
      aria-hidden="true"
    >
      <img
        src="/getalenruimte.png"
        alt=""
        className="absolute left-0 top-1 h-9 w-auto rotate-[-8deg] rounded-[3px] shadow-sm"
      />
      <img
        src="/modernewiskunde.png"
        alt=""
        className="absolute left-5 top-0 h-9 w-auto rotate-[7deg] rounded-[3px] shadow-sm"
      />
    </div>
  );
}

export function PremiumFeatureAccordionList({
  className = "",
  compact = false,
  analyticsSurface = "checkout_plan_card"
}: {
  className?: string;
  compact?: boolean;
  analyticsSurface?: string;
}) {
  const [expandedFeature, setExpandedFeature] =
    useState<PremiumFeatureKey | null>(null);
  const featureVisuals = useMemo(() => buildFeatureVisuals(), []);
  const features = useMemo(
    () =>
      premiumFeatures
        .filter((feature) => feature.key !== "huiswerkmodus")
        .sort((a, b) => {
          if (a.key === "schrift-feedback") return -1;
          if (b.key === "schrift-feedback") return 1;
          return 0;
        }),
    []
  );
  const discovered = useMemo<Record<PremiumFeatureKey, boolean>>(
    () => ({
      hint: false,
      stappenplan: false,
      bijlesbot: false,
      theorie: false,
      begripvraag: false,
      huiswerkmodus: false,
      "schrift-feedback": false,
      oefentoets: false
    }),
    []
  );

  const toggleFeatureDetails = (featureKey: PremiumFeatureKey) => {
    posthog.capture("home_feature_details_toggled", {
      feature_key: featureKey,
      surface: analyticsSurface
    });
    setExpandedFeature((currentFeature) =>
      currentFeature === featureKey ? null : featureKey
    );
  };

  return (
    <FeatureDiscoveryGroup
      features={features}
      discovered={discovered}
      expandedFeature={expandedFeature}
      featureVisuals={featureVisuals}
      onToggleFeature={toggleFeatureDetails}
      className={className}
      compact={compact}
    />
  );
}

function buildFeatureVisuals(): Record<
  PremiumFeatureKey,
  { color: string; icon: ReactNode }
> {
  const byTitle = new Map(
    FEATURE_CARDS.map((feature) => [feature.title, feature])
  );

  return {
    hint: byTitle.get("Hints") ?? {
      color: FEATURE_BG_COLORS.hints,
      icon: <LightbulbIcon />
    },
    stappenplan: byTitle.get("Stappenplan") ?? {
      color: FEATURE_BG_COLORS.stappenplan,
      icon: <ChecklistIcon />
    },
    bijlesbot: byTitle.get("Bijlesbot") ?? {
      color: FEATURE_BG_COLORS.bijlesbot,
      icon: <SpeechIcon />
    },
    theorie: byTitle.get("Theorie") ?? {
      color: FEATURE_BG_COLORS.theorie,
      icon: <BookIcon />
    },
    begripvraag: byTitle.get("Begripvraag") ?? {
      color: FEATURE_BG_COLORS.begripvraag,
      icon: <QuestionIcon />
    },
    huiswerkmodus: {
      color: "bg-teal-700",
      icon: <BookIcon />
    },
    "schrift-feedback": {
      color: "bg-rose-500",
      icon: <PenLineIcon />
    },
    oefentoets: byTitle.get("Oefentoets") ?? {
      color: FEATURE_BG_COLORS.oefentoets,
      icon: <ChecklistIcon />
    }
  };
}

function FeatureDiscoveryGroup({
  title,
  features,
  discovered,
  expandedFeature,
  featureVisuals,
  onToggleFeature,
  className = "",
  compact = false
}: {
  title?: string;
  features: typeof premiumFeatures;
  discovered: Record<PremiumFeatureKey, boolean>;
  expandedFeature: PremiumFeatureKey | null;
  featureVisuals: Record<PremiumFeatureKey, { color: string; icon: ReactNode }>;
  onToggleFeature: (featureKey: PremiumFeatureKey) => void;
  className?: string;
  compact?: boolean;
}) {
  if (features.length === 0) return null;

  return (
    <section className={className}>
      {title && (
        <div className="mb-2 flex items-center gap-2 px-1">
          <p className="text-[11px] font-bold uppercase leading-none tracking-[0.1em] text-gray-400">
            {title}
          </p>
          <div className="h-px flex-1 bg-gray-100" />
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
        {features.map((feature) => {
          const isExpanded = expandedFeature === feature.key;
          const isDiscovered = discovered[feature.key];
          const visual = featureVisuals[feature.key];

          return (
            <div
              key={feature.key}
              className="group border-b border-gray-100 last:border-b-0"
            >
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => onToggleFeature(feature.key)}
                className={`grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center text-left transition-colors ${
                  compact
                    ? "min-h-[2.875rem] gap-2 px-3 py-2.5"
                    : "min-h-[3rem] gap-3 px-3 py-2.5"
                } ${
                  isExpanded ? "bg-gray-50" : "bg-white hover:bg-gray-50"
                }`}
              >
                <FeatureMiniIcon color={visual.color} icon={visual.icon} />
                <span className="min-w-0">
                  <span className="flex min-w-0 flex-col items-start">
                    <span
                      className={`font-medium text-gray-900 ${
                        compact ? "text-[13px] leading-5" : "leading-5"
                      }`}
                    >
                      {feature.title}
                    </span>
                  </span>
                </span>
                {isDiscovered ? (
                  <FeatureDiscoveredCheck />
                ) : (
                  <AccordionChevron expanded={isExpanded} />
                )}
              </button>

              <div
                aria-hidden={!isExpanded}
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                  isExpanded
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <div
                    className={`px-3 transition-transform duration-300 ease-out ${
                      isExpanded ? "translate-y-0 pb-3" : "-translate-y-1 pb-0"
                    }`}
                  >
                    <p
                      className={`font-medium text-gray-600 ${
                        compact ? "text-xs leading-5" : "text-sm leading-6"
                      }`}
                    >
                      {feature.details}
                    </p>
                    <FeatureLocationBlock feature={feature} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FeatureLocationBlock({
  feature
}: {
  feature: (typeof premiumFeatures)[number];
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-3 flex w-full flex-col overflow-hidden rounded-lg bg-white/80 ring-1 ring-gray-200/80">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-gray-800 transition-colors hover:bg-white"
      >
        <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gray-100 text-gray-500 [&_svg]:h-3 [&_svg]:w-3">
          <LocationPinIcon />
        </span>
        <span className="min-w-0 flex-1">
          {isOpen ? "Verberg locatie" : "Waar vind je dit?"}
        </span>
        <span
          className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDownIcon />
        </span>
      </button>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden border-t border-gray-100">
          <div className="px-3 pb-3 pt-2">
            <p className="text-sm font-extrabold leading-5 text-gray-950">
              {feature.where}
            </p>
            <p className="mt-1 text-xs font-medium leading-5 text-gray-600">
              {feature.locationDetail}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeechIcon() {
  return (
    <svg
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M8 10h8m-8 4h5m8-2a8 8 0 0 1-8 8H7l-4 2 1.6-4.2A8 8 0 1 1 21 12Z"
      />
    </svg>
  );
}

function CompassIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="m15.5 8.5-2 6-6 2 2-6 6-2ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

function LocationPinIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.9}
        d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.9}
        d="M12 10.5h.01"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 12h14m-6-6 6 6-6 6"
      />
    </svg>
  );
}

function AccordionChevron({ expanded }: { expanded: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border transition-colors duration-200 ${
        expanded
          ? "border-info/25 bg-info/5 text-info"
          : "border-gray-200 bg-white text-gray-400 group-hover:border-info/25 group-hover:bg-info/5 group-hover:text-info"
      }`}
    >
      <span
        className={`transition-transform duration-300 ease-out ${
          expanded ? "rotate-180" : ""
        }`}
      >
        <ChevronDownIcon />
      </span>
    </span>
  );
}

function FeatureDiscoveredCheck() {
  return (
    <span
      aria-label="Ontdekt"
      title="Ontdekt"
      className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600"
    >
      <CheckIcon />
    </span>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.4}
        d="m6 9 6 6 6-6"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={3}
        d="m5 13 4 4L19 7"
      />
    </svg>
  );
}

function FeatureMiniIcon({ color, icon }: { color: string; icon: ReactNode }) {
  const tone = getFeatureIconTone(color);

  return (
    <span
      className={`flex h-7 w-7 flex-none items-center justify-center ${tone} [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-current [&_svg[stroke]]:stroke-current [&_svg_*[stroke]]:stroke-current`}
    >
      {icon}
    </span>
  );
}

function getFeatureIconTone(color: string) {
  const tones: Record<string, string> = {
    "bg-mathyellow": "text-mathyellow",
    "bg-info": "text-info",
    "bg-theorie": "text-theorie",
    "bg-mathorange": "text-mathorange",
    "bg-pink-600": "text-pink-600",
    "bg-emerald-500": "text-emerald-500",
    "bg-teal-700": "text-teal-700",
    "bg-rose-500": "text-rose-500"
  };

  return tones[color] ?? "text-gray-500";
}

function ChecklistIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="m9 11 2 2 4-5M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
      />
    </svg>
  );
}

function PenLineIcon({
  className = "h-4 w-4"
}: {
  className?: string;
} = {}) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 0 1 2.652 2.652L9.75 16.902 5 18l1.098-4.75L16.862 4.487ZM4 21h16"
      />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M9.1 9a3 3 0 1 1 5.34 1.86c-.9.75-1.44 1.24-1.44 2.14v.25M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function BookIcon({ className = "h-6 w-6" }: { className?: string } = {}) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function DesktopIcon() {
  return (
    <svg
      className="h-3 w-3 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17 9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2Z"
      />
    </svg>
  );
}
